import { useParams, Navigate, Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { Background3D } from '../components/Background3D';
import { getJobById } from '../data/jobs';
import { ArrowLeft, MapPin, Clock, Calendar, ExternalLink, Building2, DollarSign } from 'lucide-react';
import { JobApplicationForm } from '../components/JobApplicationForm';
import { useState } from 'react';

export function JobDetail() {
  const { theme } = useTheme();
  const { jobId } = useParams<{ jobId: string }>();
  const [showForm, setShowForm] = useState(false);
  
  if (!jobId) {
    return <Navigate to="/careers" replace />;
  }

  const job = getJobById(jobId);
  
  if (!job) {
    return <Navigate to="/careers" replace />;
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className={`relative min-h-screen ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
      <Background3D />
      <JobApplicationForm job={job} isOpen={showForm} onClose={() => setShowForm(false)} />
      
      {/* Header */}
      <section className="relative pt-24 pb-8">
        <div className="max-w-[1200px] mx-auto px-6">
          <Link
            to="/careers"
            className={`inline-flex items-center text-sm mb-8 hover:text-primary-500 transition-colors ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Careers
          </Link>
          
          <div className="mb-8">
            <h1 className="text-4xl md:text-5xl font-semibold mb-6">{job.title}</h1>
            
            <div className="flex flex-wrap gap-6 mb-6">
              <div className="flex items-center">
                <MapPin className="w-5 h-5 mr-2 text-primary-500" />
                <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
                  {job.location}
                </span>
              </div>
              <div className="flex items-center">
                <Clock className="w-5 h-5 mr-2 text-primary-500" />
                <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
                  {job.type}
                </span>
              </div>
              <div className="flex items-center">
                <Building2 className="w-5 h-5 mr-2 text-primary-500" />
                <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
                  {job.department}
                </span>
              </div>
              {job.salaryRange && (
                <div className="flex items-center">
                  <DollarSign className="w-5 h-5 mr-2 text-primary-500" />
                  <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
                    {job.salaryRange}
                  </span>
                </div>
              )}
            </div>

            {/* Remote & Voluntary Badge */}
            <div className="flex flex-wrap gap-3 mb-8">
              <span className="px-4 py-2 rounded-full text-sm bg-green-100 text-green-800 font-medium">
                🌍 Fully Remote
              </span>
              <span className="px-4 py-2 rounded-full text-sm bg-blue-100 text-blue-800 font-medium">
                🤝 Volunteer Position
              </span>
              <span className="px-4 py-2 rounded-full text-sm bg-purple-100 text-purple-800 font-medium">
                📈 Equity Participation
              </span>
            </div>

            <div className="flex flex-wrap gap-4 mb-8">
              <div className="flex items-center text-sm">
                <Calendar className="w-4 h-4 mr-2 text-primary-500" />
                <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                  Posted: {formatDate(job.postedDate)}
                </span>
              </div>
              {job.closingDate && (
                <div className="flex items-center text-sm">
                  <Calendar className="w-4 h-4 mr-2 text-orange-500" />
                  <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                    Closes: {formatDate(job.closingDate)}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center justify-center px-8 py-4 bg-primary-600 text-white rounded-full font-medium hover:bg-primary-700 transition-colors"
            >
              Apply to Volunteer
            </button>
            <button className={`px-8 py-4 rounded-full font-medium border transition-colors ${
              theme === 'dark'
                ? 'border-dark-300 text-gray-300 hover:border-primary-500 hover:text-primary-300'
                : 'border-gray-300 text-gray-700 hover:border-primary-500 hover:text-primary-600'
            }`}>
              Share Opportunity
            </button>
          </div>
        </div>
      </section>

      {/* Job Content */}
      <section className="py-12">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Description */}
              <div>
                <h2 className="text-2xl font-semibold mb-4">About this Role</h2>
                <p className={`text-lg leading-relaxed ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  {job.description}
                </p>
              </div>

              {/* Responsibilities */}
              <div>
                <h2 className="text-2xl font-semibold mb-4">What You'll Do</h2>
                <ul className="space-y-3">
                  {job.responsibilities.map((responsibility, index) => (
                    <li key={index} className="flex items-start">
                      <div className="w-2 h-2 rounded-full bg-primary-500 mr-4 mt-2 flex-shrink-0"></div>
                      <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
                        {responsibility}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Requirements */}
              <div>
                <h2 className="text-2xl font-semibold mb-4">Requirements</h2>
                <ul className="space-y-3">
                  {job.requirements.map((requirement, index) => (
                    <li key={index} className="flex items-start">
                      <div className="w-2 h-2 rounded-full bg-red-500 mr-4 mt-2 flex-shrink-0"></div>
                      <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
                        {requirement}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Qualifications */}
              <div>
                <h2 className="text-2xl font-semibold mb-4">Preferred Qualifications</h2>
                <ul className="space-y-3">
                  {job.qualifications.map((qualification, index) => (
                    <li key={index} className="flex items-start">
                      <div className="w-2 h-2 rounded-full bg-blue-500 mr-4 mt-2 flex-shrink-0"></div>
                      <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
                        {qualification}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-8">
              {/* Benefits */}
              <div className={`p-6 rounded-2xl ${
                theme === 'dark' ? 'bg-dark-200/50' : 'bg-gray-50/70'
              } backdrop-blur-sm`}>
                <h3 className="text-xl font-semibold mb-4">What We Offer</h3>
                <ul className="space-y-3">
                  {job.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start">
                      <div className="w-2 h-2 rounded-full bg-green-500 mr-3 mt-2 flex-shrink-0"></div>
                      <span className={`text-sm ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        {benefit}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Quick Apply */}
              <div className={`p-6 rounded-2xl border ${
                theme === 'dark'
                  ? 'bg-gradient-to-br from-primary-500/10 to-primary-600/5 border-primary-500/20'
                  : 'bg-gradient-to-br from-primary-50 to-primary-100/50 border-primary-200'
              }`}>
                <h3 className="text-xl font-semibold mb-4">Ready to Contribute?</h3>
                <p className={`text-sm mb-4 ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  Join our remote team and help shape the future of AI technology while earning equity in our mission.
                </p>
                <a
                  href={job.applyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center px-6 py-3 bg-primary-600 text-white rounded-full font-medium hover:bg-primary-700 transition-colors"
                >
                  Apply to Volunteer
                  <ExternalLink className="w-4 h-4 ml-2" />
                </a>
              </div>

              {/* Contact */}
              <div className={`p-6 rounded-2xl ${
                theme === 'dark' ? 'bg-dark-200/50' : 'bg-gray-50/70'
              } backdrop-blur-sm`}>
                <h3 className="text-xl font-semibold mb-4">Questions?</h3>
                <p className={`text-sm mb-4 ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  Have questions about this role? Reach out to our hiring team.
                </p>
                <a
                  href="mailto:careers@noemares.com"
                  className={`text-sm text-primary-500 hover:text-primary-600 transition-colors`}
                >
                  careers@noemares.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
